import { hasValue } from 'Lib/Any';

/**
 * Common structure for models representing quantities (a numeric value with units)
 */
export interface IQuantity {
  /**
   * value should be a number - however our API serves this up as a string in the JSON
   * To ensure consistency with the API model we must also represent this as a string
   */
  value: string;
  /**
   * Represents the units the quantity was measured in (by its abbreviation)
   */
  uom: string;
}

/**
 * Constructs an IQuantity for a given value and units
 *
 * Mainly useful to handle the conversion of the numeric values from our inputs to strings to match the API model
 */
export const toIQuantity = (value: number, uom: string): IQuantity | null =>
  hasValue(value) ? { value: value.toString(), uom } : null;

const throwIfUnitsDiffer = (arg0: IQuantity | null | undefined, arg1: IQuantity | null | undefined) => {
  if (arg0 && arg1 && arg0.uom !== arg1.uom) {
    throw new Error('Cannot compare quantities with different units.');
  }
};

export const isLessThan = (left: IQuantity | null | undefined, right: IQuantity | null | undefined) => {
  throwIfUnitsDiffer(left, right);
  if (!left || !right) return false;
  return Number(left.value) < Number(right.value);
};

export const isGreaterThan = (left: IQuantity | null | undefined, right: IQuantity | null | undefined) => {
  throwIfUnitsDiffer(left, right);
  if (!left || !right) return false;
  return Number(left.value) > Number(right.value);
};

export const isLessThanOrEqualTo = (left: IQuantity | null | undefined, right: IQuantity | null | undefined) => {
  throwIfUnitsDiffer(left, right);
  if (!left || !right) return false;
  return Number(left.value) <= Number(right.value);
};

export const isGreaterThanOrEqualTo = (left: IQuantity | null | undefined, right: IQuantity | null | undefined) => {
  throwIfUnitsDiffer(left, right);
  if (!left || !right) return false;
  return Number(left.value) >= Number(right.value);
};
